import { useEffect, RefObject } from 'react';

/**
 * Syncs Affirm BNPL promo content from the primary element to a target element.
 * Uses MutationObserver to mirror content without triggering duplicate Affirm API calls.
 * Also intercepts clicks on the target element to trigger the Affirm modal via the source element.
 */
export function useAffirmBnplPromoSync(targetRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const sourceEl = document.getElementById('affirm-as-low-as-payment-section');
    const targetEl = targetRef.current;
    if (!sourceEl || !targetEl) return;

    // Initial sync
    targetEl.innerHTML = sourceEl.innerHTML;

    // Observe changes to the source element
    const observer = new MutationObserver(() => {
      if (targetEl) targetEl.innerHTML = sourceEl.innerHTML;
    });
    observer.observe(sourceEl, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    // Intercept clicks on the target element and delegate to the source element's anchor.
    // Required to open the modal.
    const handleClick = (e: MouseEvent) => {
      try {
        const clickedAnchor = (e.target as HTMLElement).closest('a');
        if (clickedAnchor) {
          e.preventDefault();
          e.stopPropagation();
          // Find the corresponding anchor in the source element and click it
          const sourceAnchor = sourceEl.querySelector('a');
          if (sourceAnchor) {
            sourceAnchor.click();
          }
        }
      } catch {
        // Prevent errors from bubbling up and breaking other app logic
      }
    };

    targetEl.addEventListener('click', handleClick);

    return () => {
      observer.disconnect();
      targetEl.removeEventListener('click', handleClick);
    };
  }, [targetRef]);
}
